import type { LiveBlock, Severity, TokenomicsSummary } from "../../shared/types";

export interface LiveMetricTotals {
  mintedWei: bigint;
  burnedWei: bigint;
  authorizedWei: bigint;
}

export interface LiveMetricAnimationKeys {
  minted: number;
  burned: number;
  authorized: number;
  net: number;
  burnPulse: number;
}

export interface LiveMetricPresentation {
  totals: LiveMetricTotals;
  animationKeys: LiveMetricAnimationKeys;
}

export function netSupplyChangeWei(totals: LiveMetricTotals): bigint {
  return totals.mintedWei - totals.burnedWei;
}

export function createLiveMetricPresentation(totals: LiveMetricTotals): LiveMetricPresentation {
  return {
    totals,
    animationKeys: { minted: 0, burned: 0, authorized: 0, net: 0, burnPulse: 0 },
  };
}

export function presentLiveMetricTotals(
  current: LiveMetricPresentation,
  totals: LiveMetricTotals,
  severity: Severity = "routine",
): LiveMetricPresentation {
  const currentNet = netSupplyChangeWei(current.totals);
  const nextNet = netSupplyChangeWei(totals);
  const burnedMore = totals.burnedWei > current.totals.burnedWei;
  return {
    totals,
    animationKeys: {
      minted: current.animationKeys.minted + Number(totals.mintedWei > current.totals.mintedWei),
      burned: current.animationKeys.burned + Number(burnedMore),
      authorized: current.animationKeys.authorized + Number(totals.authorizedWei > current.totals.authorizedWei),
      net: current.animationKeys.net + Number(nextNet !== currentNet),
      burnPulse: current.animationKeys.burnPulse + Number(burnedMore && severity !== "routine"),
    },
  };
}

export function rebaseLiveMetricTotals(current: LiveMetricPresentation, totals: LiveMetricTotals): LiveMetricPresentation {
  return { ...current, totals };
}

export function buildLiveMetricTotals(summary: TokenomicsSummary, blocks: LiveBlock[]): LiveMetricTotals {
  const through = BigInt(summary.completeThroughBlock);
  const pending = new Map<string, LiveBlock>();
  for (const block of blocks) {
    if (BigInt(block.blockNumber) > through) pending.set(block.blockNumber, block);
  }
  let mintedWei = BigInt(summary.realizedMintedWei);
  let burnedWei = BigInt(summary.irreversiblyBurnedWei);
  let authorizedWei = BigInt(summary.authorizedInflationWei);
  for (const block of pending.values()) {
    mintedWei += BigInt(block.mintedWei);
    burnedWei += BigInt(block.totalBurnWei);
    authorizedWei += BigInt(block.authorizedInflationWei);
  }
  return { mintedWei, burnedWei, authorizedWei };
}

export function burnCoverageBps(mintedWei: bigint, burnedWei: bigint): string | null {
  return mintedWei > 0n ? ((burnedWei * 10_000n + mintedWei / 2n) / mintedWei).toString() : null;
}
